/**
 * Compose and reply, from the Sales feed (G1c).
 *
 * ---------------------------------------------------------------------------
 * IT IS NOT A THIRD MODE OF "ADD TO LOG"
 * ---------------------------------------------------------------------------
 * The note composer already creates a note and/or a follow-up from one button.
 * Adding email to it would make one control do three unrelated things and make
 * every one of them harder to explain. This is its own panel behind its own
 * button, and notes and follow-ups are untouched by it.
 *
 * ---------------------------------------------------------------------------
 * A MISSING MERGE VALUE IS HIGHLIGHTED AND THEN BLOCKS THE SEND
 * ---------------------------------------------------------------------------
 * Inserting a template fills what is known and leaves what is not as a visible,
 * amber token. The rep types the answer or deletes the sentence. If any token is
 * still there at send time the send is REFUSED, by name.
 *
 * That refusal is the point of the whole feature. Substituting an unknown value
 * with an empty string produces "Hi ," and "your move from  to  on " — sentences
 * that are grammatically whole, obviously machine-written, and invisible to the
 * person who sent them. A rep who has to fix it before sending is the cheapest
 * possible place to catch it.
 *
 * ---------------------------------------------------------------------------
 * PLAIN FORMATTING ONLY
 * ---------------------------------------------------------------------------
 * Bold, italic, links, lists. execCommand is deprecated and is still the only
 * thing every browser implements for this; the alternative is a rich-text editor
 * dependency larger than this entire feature, to produce email HTML that Outlook
 * will re-render its own way regardless.
 */

import React, { useEffect, useMemo, useRef, useState } from 'react';
import { CRM } from '../lib/theme';
import { useApi, useApiClient } from '../hooks/useApi';
import { MERGE_FIELDS, fillMergeFields, hasUnfilledFields, fieldLabel, needsLeadingSpace } from '../lib/mergeFields';
import { Icons } from '../lib/icons';
import { useAction } from '../hooks/useAction';

const C = { ...CRM, mono: "'DM Mono', monospace" };

const input: React.CSSProperties = {
  width: '100%', padding: '7px 9px', background: C.panel2, border: `1px solid ${C.border}`,
  color: C.text, borderRadius: '6px', fontSize: '12px', boxSizing: 'border-box',
};
const ghost: React.CSSProperties = {
  padding: '5px 10px', background: C.panel2, color: C.muted,
  border: `1px solid ${C.border}`, borderRadius: '6px', cursor: 'pointer', fontSize: '11px',
};

const MAX_TOTAL = 25 * 1024 * 1024;
const fmtBytes = (n: number) =>
  n < 1024 ? `${n} B` : n < 1024 * 1024 ? `${Math.round(n / 1024)} KB` : `${(n / 1024 / 1024).toFixed(1)} MB`;

/** The amber wrapper an unfilled token wears. Stripped before anything is sent. */
const MISSING_OPEN = '<span data-mf-missing="1" style="background:#f59e0b33;color:#f59e0b;border-radius:3px;padding:0 2px">';
/**
 * Remove the amber wrapper, leaving whatever the rep typed inside it.
 *
 * Only ever runs on a body that has already passed the unfilled-token guard, so
 * what is inside these spans is a real value the rep typed over the token — not
 * a token about to escape into a client's inbox.
 */
const stripMissingMarkup = (html: string) =>
  String(html ?? '').replace(/<span data-mf-missing="1"[^>]*>([\s\S]*?)<\/span>/g, '$1');

export interface ReplyContext {
  message: any;
  all: boolean;
}

export function ComposeEmail({
  leadId, lead, capability, replyTo, onClose, onSent,
}: {
  leadId: string;
  lead: any;
  capability: { canSend: boolean; reason: string; fromAddress: string; fromName: string } | null;
  replyTo?: ReplyContext | null;
  onClose: () => void;
  onSent: () => void;
}) {
  const api = useApiClient();
  const bodyRef = useRef<HTMLDivElement>(null);

  const { data: templates, refetch: refetchTemplates } = useApi<any>(
    async () => (await api.getEmailTemplates()).data
  );
  const { data: merge } = useApi<Record<string, string | null>>(
    async () => (await api.getMergeFields(leadId)).data ?? {},
    { dependencies: [leadId] }
  );

  // Reply prefill. Reply goes to whoever wrote it; Reply all adds everyone who
  // was on it, minus ourselves — being copied on your own reply is noise, and
  // it is how a two-person thread quietly becomes a four-person one.
  const prefill = useMemo(() => {
    const m = replyTo?.message;
    if (!m) {
      return { to: [String(lead?.email ?? '').trim()].filter(Boolean), cc: [] as string[], subject: '' };
    }
    const me = String(capability?.fromAddress ?? '').toLowerCase();
    const drop = (list: string[]) => list.filter((a) => a && a.toLowerCase() !== me);
    const to = drop([m.fromAddress].filter(Boolean));
    const cc = replyTo?.all ? drop([...(m.to ?? []), ...(m.cc ?? [])]).filter((a) => !to.includes(a)) : [];
    const subj = String(m.subject ?? '');
    return { to, cc, subject: /^re:/i.test(subj) ? subj : `Re: ${subj}` };
  }, [replyTo, lead, capability]);

  const [to, setTo] = useState(prefill.to.join(', '));
  const [cc, setCc] = useState(prefill.cc.join(', '));
  const [showCc, setShowCc] = useState(prefill.cc.length > 0);
  const [subject, setSubject] = useState(prefill.subject);
  const [files, setFiles] = useState<File[]>([]);
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState('');
  const [missing, setMissing] = useState<string[]>([]);
  const [saveName, setSaveName] = useState('');
  const [showSave, setShowSave] = useState(false);

  useEffect(() => {
    // Focus the body on open — the addresses are usually already right.
    bodyRef.current?.focus();
  }, []);

  const exec = (cmd: string, arg?: string) => {
    bodyRef.current?.focus();
    document.execCommand(cmd, false, arg);
  };

  const insertHtml = (html: string) => {
    bodyRef.current?.focus();
    document.execCommand('insertHTML', false, html);
  };

  /**
   * Insert a merge value WITH THE SPACES A SENTENCE NEEDS.
   *
   * Inserting "client first name" and then "job #" produced "Daryl#1000",
   * because execCommand drops the text exactly at the caret and a rep picking
   * two fields in a row never types the gap between them. So: a leading space
   * unless the character before the caret is already whitespace (or the field
   * starts the message), and always a trailing one — which also puts the caret
   * where the next word goes, so the rep can simply keep typing.
   */
  const insertField = (html: string) => {
    let before = '';
    try {
      const sel = window.getSelection();
      const node = sel?.anchorNode;
      if (node?.nodeType === Node.TEXT_NODE) {
        before = String(node.textContent ?? '').slice(0, sel?.anchorOffset ?? 0);
      }
    } catch {
      /* no selection to read — treat as the start of a block */
    }
    insertHtml(`${needsLeadingSpace(before) ? '&nbsp;' : ''}${html}&nbsp;`);
  };

  const applyTemplate = (t: any) => {
    const filledSubject = fillMergeFields(t.subject ?? '', merge);
    const filledBody = fillMergeFields(t.body ?? '', merge);
    if (filledSubject.text) setSubject(filledSubject.text);

    // Unfilled tokens go in wearing the amber wrapper so they are impossible to
    // scroll past. The wrapper never leaves the browser: the send is refused
    // while any token remains.
    const highlighted = filledBody.text.replace(
      /\{\{\s*[a-z_]+\s*\}\}/gi,
      (tok) => `${MISSING_OPEN}${tok}</span>`
    );
    if (bodyRef.current) bodyRef.current.innerHTML = highlighted.replace(/\n/g, '<br>');
    setMissing([...new Set([...filledSubject.missing, ...filledBody.missing])]);
  };

  const totalBytes = files.reduce((n, f) => n + f.size, 0);
  const tooBig = totalBytes > MAX_TOTAL;

  const sendGuard = useAction(); // one at a time: a second click is ignored
  const send = () => sendGuard.run(() => sendNow());
  const sendNow = async () => {
    setErr('');
    const html = bodyRef.current?.innerHTML ?? '';
    const plain = bodyRef.current?.innerText ?? '';

    if (!to.trim()) { setErr('Add at least one recipient.'); return; }
    if (!subject.trim()) { setErr('Add a subject.'); return; }
    if (!plain.trim()) { setErr('The message is empty.'); return; }

    // THE GUARD. Named fields, not a generic warning — "fill in the blanks" is
    // useless when the blank is three paragraphs down.
    if (hasUnfilledFields(plain)) {
      const left = fillMergeFields(plain, merge).missing;
      setErr(
        `Still to fill in: ${left.map(fieldLabel).join(', ')}. ` +
        `Type the value or delete that sentence — the client would otherwise get a gap where it should be.`
      );
      return;
    }
    if (tooBig) {
      setErr(`Attachments total ${fmtBytes(totalBytes)}. Gmail's limit is 25 MB, and encoding adds about a third.`);
      return;
    }

    setBusy(true);
    try {
      await api.sendLeadEmail(leadId, {
        to: to.split(/[,;]/).map((x) => x.trim()).filter(Boolean),
        cc: cc.split(/[,;]/).map((x) => x.trim()).filter(Boolean),
        subject: subject.trim(),
        bodyHtml: stripMissingMarkup(html),
        replyToMessageId: replyTo?.message?.id ?? null,
        attachments: files,
      });
      onSent();
      onClose();
    } catch (e: any) {
      setErr(e?.message || 'The email was not sent.');
    } finally {
      setBusy(false);
    }
  };

  const saveAsTemplateGuard = useAction(); // one at a time: a second click is ignored
  const saveAsTemplate = () => saveAsTemplateGuard.run(() => saveAsTemplateNow());
  const saveAsTemplateNow = async () => {
    const name = saveName.trim();
    if (!name) return;
    try {
      await api.createEmailTemplate({
        name,
        subject,
        // Saved as WRITTEN, tokens and all: a template whose merge fields were
        // resolved to this one client is not a template, it is a copy of one
        // email with somebody else's name baked into it.
        body: bodyRef.current?.innerHTML ?? '',
      });
      setShowSave(false);
      setSaveName('');
      refetchTemplates();
    } catch (e: any) {
      setErr(e?.message || 'Could not save that template.');
    }
  };

  // The panel refuses to open at all when sending is unavailable, but the guard
  // is repeated here so a future caller cannot bypass it by rendering directly.
  if (capability && !capability.canSend) {
    return (
      <div style={{ background: C.panel, border: `1px solid ${C.amberSoftLine}`, borderRadius: '10px', padding: '14px', marginBottom: '12px' }}>
        <div style={{ fontSize: '12px', color: C.amber, lineHeight: 1.6 }}>{capability.reason}</div>
        <button onClick={onClose} style={{ ...ghost, marginTop: '10px' }}>Close</button>
      </div>
    );
  }

  const shared: any[] = templates?.shared ?? [];
  const mine: any[] = templates?.mine ?? [];

  return (
    <div style={{ background: C.panel, border: `1px solid ${C.accentSoftLine}`, borderRadius: '10px', padding: '12px', marginBottom: '12px' }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '10px' }}>
        <span style={{ fontSize: '13px', fontWeight: 700, color: C.text }}>
          {replyTo ? (replyTo.all ? 'Reply all' : 'Reply') : 'New email'}
        </span>
        <span style={{ fontSize: '11px', color: C.dim, fontFamily: C.mono }}>
          from {capability?.fromAddress ?? '—'}
        </span>
        <button onClick={onClose} style={{ marginLeft: 'auto', background: 'none', border: 'none', color: C.muted, cursor: 'pointer' }} aria-label="Close">
          <Icons.close size={15} />
        </button>
      </div>

      <div style={{ display: 'grid', gap: '7px' }}>
        <div style={{ display: 'flex', gap: '7px', alignItems: 'center' }}>
          <input style={input} value={to} onChange={(e) => setTo(e.target.value)} placeholder="To" />
          {!showCc && <button style={ghost} onClick={() => setShowCc(true)}>Cc</button>}
        </div>
        {showCc && <input style={input} value={cc} onChange={(e) => setCc(e.target.value)} placeholder="Cc" />}
        <input style={input} value={subject} onChange={(e) => setSubject(e.target.value)} placeholder="Subject" />
      </div>

      {/* Toolbar: formatting, templates, merge fields */}
      <div style={{ display: 'flex', gap: '6px', alignItems: 'center', flexWrap: 'wrap', margin: '9px 0 7px' }}>
        <button style={{ ...ghost, fontWeight: 700 }} onMouseDown={(e) => { e.preventDefault(); exec('bold'); }} title="Bold">B</button>
        <button style={{ ...ghost, fontStyle: 'italic' }} onMouseDown={(e) => { e.preventDefault(); exec('italic'); }} title="Italic">I</button>
        <button style={ghost} onMouseDown={(e) => { e.preventDefault(); exec('insertUnorderedList'); }} title="Bulleted list">• List</button>
        <button
          style={ghost}
          onMouseDown={(e) => {
            e.preventDefault();
            const url = window.prompt('Link to:');
            if (url) exec('createLink', url);
          }}
          title="Insert a link"
        >
          Link
        </button>

        <span style={{ width: '1px', height: '18px', background: C.border }} />

        <select
          style={{ ...ghost, maxWidth: '190px' }}
          value=""
          onChange={(e) => {
            const t = [...shared, ...mine].find((x) => x.id === e.target.value);
            if (t) applyTemplate(t);
          }}
        >
          <option value="">Template…</option>
          {shared.length > 0 && (
            <optgroup label="Shared">
              {shared.map((t) => <option key={t.id} value={t.id}>{t.name}</option>)}
            </optgroup>
          )}
          {mine.length > 0 && (
            <optgroup label="Mine">
              {mine.map((t) => <option key={t.id} value={t.id}>{t.name}</option>)}
            </optgroup>
          )}
        </select>

        <select
          style={{ ...ghost, maxWidth: '170px' }}
          value=""
          onChange={(e) => {
            const f = MERGE_FIELDS.find((x) => x.key === e.target.value);
            if (!f) return;
            const v = merge?.[f.key];
            // Inserted FILLED where we know the answer, highlighted where we do
            // not — the same rule the template picker uses.
            if (v != null && String(v).trim()) insertField(String(v));
            else { insertField(`${MISSING_OPEN}${f.token}</span>`); setMissing((m) => [...new Set([...m, f.key])]); }
          }}
        >
          <option value="">Insert field…</option>
          {MERGE_FIELDS.map((f) => (
            <option key={f.key} value={f.key}>
              {f.label}{merge?.[f.key] == null ? ' (not set)' : ''}
            </option>
          ))}
        </select>
      </div>

      <div
        ref={bodyRef}
        contentEditable
        suppressContentEditableWarning
        style={{
          minHeight: '150px', maxHeight: '420px', overflowY: 'auto',
          background: C.panel2, border: `1px solid ${C.border}`, borderRadius: '7px',
          padding: '10px', fontSize: '12.5px', lineHeight: 1.6, color: C.text, outline: 'none',
        }}
      />

      {missing.length > 0 && (
        <div style={{ marginTop: '7px', fontSize: '11px', color: C.amber, lineHeight: 1.5 }}>
          Highlighted and still to fill in: {missing.map(fieldLabel).join(', ')}.
          {missing.includes('sender_phone') && ' Your phone lives in Settings → My Account.'}
          {missing.includes('sender_title') && ' Your title lives in Settings → My Account.'}
        </div>
      )}

      {/* Attachments */}
      <div style={{ display: 'flex', gap: '7px', alignItems: 'center', flexWrap: 'wrap', marginTop: '9px' }}>
        <label style={{ ...ghost, display: 'inline-flex', alignItems: 'center', gap: '5px' }}>
          <Icons.download size={12} /> Attach
          <input
            type="file" multiple style={{ display: 'none' }}
            onChange={(e) => { setFiles((f) => [...f, ...Array.from(e.target.files ?? [])]); e.currentTarget.value = ''; }}
          />
        </label>
        {files.map((f, i) => (
          <span key={`${f.name}-${i}`} style={{ display: 'inline-flex', alignItems: 'center', gap: '5px', background: C.panel2, border: `1px solid ${C.border}`, borderRadius: '6px', padding: '4px 8px', fontSize: '11px', color: C.text }}>
            {f.name} <span style={{ color: C.dim }}>{fmtBytes(f.size)}</span>
            <button onClick={() => setFiles((x) => x.filter((_, j) => j !== i))} style={{ background: 'none', border: 'none', color: C.muted, cursor: 'pointer', padding: 0 }} aria-label={`Remove ${f.name}`}>
              <Icons.close size={12} />
            </button>
          </span>
        ))}
        {files.length > 0 && (
          <span style={{ fontSize: '11px', color: tooBig ? C.red : C.dim }}>
            {fmtBytes(totalBytes)} of 25 MB
          </span>
        )}
      </div>

      {err && (
        <div style={{ marginTop: '9px', background: `${C.redTint}`, border: `1px solid ${C.redSoftLine}`, color: C.red, borderRadius: '7px', padding: '8px 10px', fontSize: '11.5px', lineHeight: 1.5 }}>
          {err}
        </div>
      )}

      <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginTop: '10px', flexWrap: 'wrap' }}>
        <button onClick={send} disabled={busy} style={{ padding: '7px 16px', background: C.accentFill, color: 'var(--crm-on-fill)', border: 'none', borderRadius: '7px', cursor: busy ? 'default' : 'pointer', fontSize: '12px', fontWeight: 600, opacity: busy ? 0.6 : 1 }}>
          {busy ? 'Sending…' : replyTo ? 'Send reply' : 'Send'}
        </button>
        {!showSave && <button style={ghost} onClick={() => setShowSave(true)}>Save as my template</button>}
        {showSave && (
          <>
            <input style={{ ...input, width: '180px' }} value={saveName} onChange={(e) => setSaveName(e.target.value)} placeholder="Template name" autoFocus />
            <button style={ghost} onClick={saveAsTemplate} disabled={!saveName.trim()}>Save</button>
            <button style={ghost} onClick={() => setShowSave(false)}>Cancel</button>
          </>
        )}
        <span style={{ marginLeft: 'auto', fontSize: '10.5px', color: C.dim }}>
          Sent from your own Gmail · your MoveFlow signature is added automatically
        </span>
      </div>
    </div>
  );
}
